<script setup>
// El carrusel de la ficha. Un carril con scroll-snap y nada más: el navegador
// ya sabe desplazarse con inercia, con dos dedos y con la rueda, así que la
// única JavaScript que hay acá es la que mueve el carril cuando alguien aprieta
// una flecha y la que lee en qué foto quedó.
//
// Con una sola foto no dibuja flechas ni puntos: un carrusel de una foto es una
// foto.
import { computed, ref, watch } from 'vue'

const props = defineProps({
  photos: { type: Array, default: () => [] },
  // Para el panel de relleno cuando el producto todavía no tiene fotos: la
  // misma escala de luz que usa la tarjeta.
  light: { type: Number, default: 0 },
  label: { type: String, default: '' }
})

const track = ref(null)
const index = ref(0)

const many = computed(() => props.photos.length > 1)
const glow = computed(() => 0.05 + (props.light / 5) * 0.3)

// Al cambiar de producto el carril vuelve al principio sin animación: la ficha
// recién se abre, no hay nada que "recorrer" hacia atrás.
watch(() => props.photos, () => {
  index.value = 0
  track.value?.scrollTo({ left: 0, behavior: 'instant' })
})

function go (to) {
  const el = track.value
  if (!el) return

  const next = Math.min(Math.max(to, 0), props.photos.length - 1)
  el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' })
  index.value = next
}

// El índice sale de dónde quedó el scroll, no de quién apretó qué: así arrastrar
// con el dedo mueve los puntos igual que las flechas.
function onScroll () {
  const el = track.value
  if (!el || !el.clientWidth) return

  index.value = Math.round(el.scrollLeft / el.clientWidth)
}
</script>

<template>
  <div class="relative flex h-full flex-col">
    <div
      v-if="photos.length"
      ref="track"
      class="flex aspect-4/5 min-h-0 shrink snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-md bg-night/40 sm:aspect-square lg:aspect-auto lg:flex-1"
      :aria-label="`Fotos de ${label}`"
      tabindex="0"
      @scroll.passive="onScroll"
      @keydown.left.prevent="go(index - 1)"
      @keydown.right.prevent="go(index + 1)"
    >
      <img
        v-for="(photo, position) in photos"
        :key="photo.src"
        :src="photo.src"
        :alt="photo.alt"
        :width="photo.width"
        :height="photo.height"
        :loading="position === 0 ? 'eager' : 'lazy'"
        decoding="async"
        class="h-full w-full shrink-0 snap-center object-cover"
      />
    </div>

    <!-- Sin fotos, el mismo panel que dibuja la tarjeta: un dibujo, no una
         imagen rota. -->
    <div
      v-else
      class="photo-slot aspect-4/5 w-full rounded-md sm:aspect-square lg:aspect-auto lg:flex-1"
      :style="{ '--glow': glow }"
      aria-hidden="true"
    ></div>

    <template v-if="many">
      <button
        type="button"
        class="carousel-arrow start-3"
        :disabled="index === 0"
        aria-label="Foto anterior"
        @click="go(index - 1)"
      >
        <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5" aria-hidden="true">
          <path d="m15 5-7 7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <button
        type="button"
        class="carousel-arrow end-3"
        :disabled="index === photos.length - 1"
        aria-label="Foto siguiente"
        @click="go(index + 1)"
      >
        <svg viewBox="0 0 24 24" fill="none" class="h-5 w-5" aria-hidden="true">
          <path d="m9 5 7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="mt-3 flex shrink-0 items-center justify-center gap-2">
        <button
          v-for="(photo, position) in photos"
          :key="`dot-${photo.src}`"
          type="button"
          class="h-2 rounded-full transition-all duration-300"
          :class="position === index ? 'w-6 bg-glow' : 'w-2 bg-linen/30 hover:bg-linen/60'"
          :aria-label="`Ver la foto ${position + 1} de ${photos.length}`"
          :aria-current="position === index"
          @click="go(position)"
        ></button>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* La barra de scroll del carril sobra: los puntos y las flechas ya dicen dónde
   se está. */
div[tabindex] {
  scrollbar-width: none;
}

div[tabindex]::-webkit-scrollbar {
  display: none;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  display: inline-flex;
  height: 2.5rem;
  width: 2.5rem;
  transform: translateY(-50%);
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background-color: rgb(21 16 13 / 0.65);
  color: var(--color-linen);
  backdrop-filter: blur(4px);
  transition: opacity 300ms var(--ease-soft), background-color 300ms var(--ease-soft);
}

.carousel-arrow:hover {
  background-color: rgb(21 16 13 / 0.9);
}

.carousel-arrow:disabled {
  pointer-events: none;
  opacity: 0;
}

/* El panel de relleno de un producto sin fotos, igual que en la tarjeta. */
.photo-slot {
  background-color: var(--color-tobacco);
  background-image:
    linear-gradient(
      180deg,
      rgb(232 167 92 / var(--glow)) 0%,
      rgb(232 167 92 / calc(var(--glow) * 0.25)) 45%,
      transparent 78%
    ),
    repeating-linear-gradient(
      180deg,
      rgb(243 236 226 / 0.05) 0 1px,
      transparent 1px 13px
    );
}
</style>
